import { useEffect, useState } from 'react';
import type {
  CreditEntryKind,
  PanelActorDto,
  PanelCreditGrantDto,
  PanelCreditLedgerEntryDto,
} from '@icebreaker/shared';
import {
  Avatar,
  Button,
  Skeleton,
  Table,
  Tag,
  TruncatedText,
  type TagVariant,
} from '@icebreaker/ui';
import { numericDateOptions } from '@/features/sales/model/sales-view';
import { useTranslation } from '@/shared/i18n/i18n-provider';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import type { RootState } from '@/shared/store/store';
import { LEDGER_PAGE_SIZE } from '../store/organizations.saga';
import { fetchCreditLedger, type LedgerHighlight } from '../store/credits.slice';
import { ReverseCreditsDialog } from './reverse-credits-dialog';
import styles from './organizations.module.css';

/** Defter türü → etiket tonu (referans `led()` tablosu). */
const KIND_VARIANT: Readonly<Record<CreditEntryKind, TagVariant>> = {
  grant: 'success',
  consume: 'neutral',
  consume_contract: 'accent',
  refund: 'success',
  expire: 'warning',
  reversal: 'neutral',
};

/** Sunucunun deneme kredisine yazdığı makine gerekçesi (C1) — ekrana ham yazılmaz. */
const TRIAL_PROVISION_REASON = 'panel_provision';

/**
 * KREDİ DEFTERİ (P5 bölüm 5).
 *
 * Deneme kredisinin yükleme satırında "Ters kaydet" ÇİZİLMEZ (referans
 * defter satırları): deneme kredisi yalnız kurum açılışında yazılır ve
 * panelden yeniden yüklenemez, ters kaydı geri alınamaz bir silme olurdu. Append-only: satır silinmez, düzeltme yalnız
 * ters kayıtla. Her satırda kim (personel ya da kullanıcı avatarı + adı, ya da
 * "Sistem"), ne zaman ve neden okunur. Yükleme satırında kullanılmamış kısmı
 * olan hak için "Ters kaydet" çizilir; yeni satır başarıdan sonra vurgulanır.
 */
export function CreditLedgerCard({ organizationId }: { organizationId: string }) {
  const t = useTranslation();
  const dispatch = useAppDispatch();
  const ledger = useAppSelector((root: RootState) => root.panelCredits.ledger);
  const highlight = useAppSelector((root: RootState) => root.panelCredits.highlight);
  const overview = useAppSelector((root: RootState) => root.panelCredits.overview);
  const [reversing, setReversing] = useState<PanelCreditGrantDto | null>(null);

  useEffect(() => {
    dispatch(fetchCreditLedger.request({ organizationId, page: 1, pageSize: LEDGER_PAGE_SIZE }));
  }, [dispatch, organizationId]);

  const ownLedger = ledger.organizationId === organizationId;
  const items = ownLedger ? ledger.items : [];
  const grants = overview.organizationId === organizationId ? (overview.data?.grants ?? []) : [];
  const pageSize = ledger.pageSize || LEDGER_PAGE_SIZE;
  const from = ledger.total === 0 ? 0 : (ledger.page - 1) * pageSize + 1;
  const to = Math.min(ledger.page * pageSize, ledger.total);
  const onlyTrial =
    ownLedger &&
    ledger.phase === 'succeeded' &&
    ledger.total === 1 &&
    items[0]?.kind === 'grant' &&
    items[0]?.grantKind === 'trial';
  const goTo = (page: number) =>
    dispatch(fetchCreditLedger.request({ organizationId, page, pageSize }));

  return (
    <section className={styles.ledgerCard} aria-labelledby="credit-ledger-title">
      <div className={styles.ledgerHead}>
        <h2 id="credit-ledger-title" className={styles.cardTitle}>
          {t.t('admin.credit.ledger.title')}
        </h2>
        <span className={styles.ledgerNote}>{t.t('admin.credit.ledger.immutable')}</span>
      </div>
      {ledger.phase === 'failed' && items.length === 0 ? (
        <div className={[styles.ledgerState, styles.sectionState].join(' ')} role="alert">
          <p className={styles.sectionError}>{t.t('admin.credit.ledger.loadError')}</p>
          <Button variant="secondary" size="sm" onClick={() => goTo(ledger.page)}>
            {t.t('admin.orgs.retry')}
          </Button>
        </div>
      ) : items.length === 0 && ownLedger && ledger.phase === 'succeeded' ? (
        <p className={styles.ledgerOnly}>{t.t('admin.credit.ledger.empty')}</p>
      ) : items.length === 0 ? (
        <div className={styles.ledgerState}>
          <Skeleton label={t.t('admin.credit.ledger.loading')} lines={4} />
        </div>
      ) : (
        <div className={[styles.ledgerTable, 'ib-priority-table'].join(' ')}>
          <Table aria-label={t.t('admin.credit.ledger.title')}>
            <thead>
              <tr>
                <th>{t.t('admin.credit.ledger.at')}</th>
                <th>{t.t('admin.credit.ledger.kind')}</th>
                <th className={styles.numberHead}>{t.t('admin.credit.ledger.amount')}</th>
                <th>{t.t('admin.credit.ledger.event')}</th>
                <th>{t.t('admin.credit.ledger.actor')}</th>
                <th className={[styles.refCol, 'ib-col-optional'].join(' ')}>
                  {t.t('admin.credit.ledger.reference')}
                </th>
                <th>{t.t('admin.credit.ledger.reason')}</th>
                <th aria-label={t.t('admin.credit.ledger.actions')} />
              </tr>
            </thead>
            <tbody>
              {items.map((entry) => {
                const grant =
                  entry.kind === 'grant' && entry.grantId
                    ? (grants.find((row) => row.id === entry.grantId) ?? null)
                    : null;
                return (
                  <LedgerRow
                    key={entry.id}
                    entry={entry}
                    highlighted={isHighlighted(entry, highlight)}
                    reversibleGrant={
                      grant && grant.kind !== 'trial' && grant.reversible > 0 ? grant : null
                    }
                    onReverse={setReversing}
                  />
                );
              })}
            </tbody>
          </Table>
          {onlyTrial ? (
            <p className={styles.ledgerOnly}>{t.t('admin.credit.ledger.onlyTrial')}</p>
          ) : null}
        </div>
      )}
      {ledger.total > 0 ? (
        <div className={styles.ledgerFoot}>
          <span className={styles.pagerRange}>
            {t.t('admin.credit.ledger.range', {
              from: t.formatNumber(from),
              to: t.formatNumber(to),
              total: t.formatNumber(ledger.total),
            })}
          </span>
          {ledger.page > 1 ? (
            <Button variant="secondary" size="sm" onClick={() => goTo(ledger.page - 1)}>
              {t.t('admin.overview.prevPage')}
            </Button>
          ) : null}
          {ledger.hasNext ? (
            <Button variant="secondary" size="sm" onClick={() => goTo(ledger.page + 1)}>
              {t.t('admin.overview.nextPage')}
            </Button>
          ) : null}
        </div>
      ) : null}
      {reversing ? (
        <ReverseCreditsDialog
          organizationId={organizationId}
          grant={reversing}
          onClose={() => setReversing(null)}
        />
      ) : null}
    </section>
  );
}

function isHighlighted(entry: PanelCreditLedgerEntryDto, highlight: LedgerHighlight | null) {
  if (!highlight) return false;
  if (highlight.kind === 'entry') return entry.id === highlight.id;
  return entry.kind === 'grant' && entry.grantId === highlight.id;
}

function LedgerRow({
  entry,
  highlighted,
  reversibleGrant,
  onReverse,
}: {
  entry: PanelCreditLedgerEntryDto;
  highlighted: boolean;
  reversibleGrant: PanelCreditGrantDto | null;
  onReverse: (grant: PanelCreditGrantDto) => void;
}) {
  const t = useTranslation();
  const amountClass =
    entry.amount > 0 ? styles.amountPlus : entry.amount === 0 ? styles.amountZero : '';
  const reference = entry.reference ?? '—';
  return (
    <tr className={highlighted ? styles.ledgerRowHighlight : undefined}>
      <td className={styles.dateCell}>
        <span className={styles.ledgerDate}>
          {t.formatDate(entry.createdAt, numericDateOptions(t.locale))}
        </span>
        <span className={styles.ledgerTime}>
          {t.formatDate(entry.createdAt, { hour: '2-digit', minute: '2-digit' })}
        </span>
      </td>
      <td>
        <span className={styles.kindTags}>
          <Tag variant={KIND_VARIANT[entry.kind]} className={styles.kindTag}>
            {t.t(`admin.credit.ledger.kinds.${entry.kind}`)}
          </Tag>
          {/* Hediye hakkının satırları (referans kodu Story 4); ödenen hakta yok. */}
          {entry.giftReferralCode ? (
            <Tag variant="speaker" className={[styles.kindTag, styles.giftTag].join(' ')}>
              {/* 32 karakterlik kod Tür kolonunu tablonun dışına iter: kesilir, tamamı ipucuyla okunur. */}
              <TruncatedText className={styles.giftTagText}>
                {t.t('admin.credit.ledger.giftTag', { code: entry.giftReferralCode })}
              </TruncatedText>
            </Tag>
          ) : null}
        </span>
      </td>
      <td className={[styles.numberCell, amountClass].join(' ')}>{signedAmount(entry.amount)}</td>
      <td className={styles.eventCell}>{entry.event?.title ?? '—'}</td>
      <td>
        <ActorCell actor={entry.actor} />
      </td>
      <td className={[styles.refCell, styles.refCol, 'ib-col-optional'].join(' ')}>{reference}</td>
      <td className={styles.reasonCell}>
        {reasonText(t, entry)}
        {entry.reference ? (
          <span className={[styles.refInline, 'ib-col-note'].join(' ')}>{entry.reference}</span>
        ) : null}
      </td>
      <td className={styles.actionCell}>
        {reversibleGrant ? (
          <Button variant="dangerOutline" size="sm" onClick={() => onReverse(reversibleGrant)}>
            {t.t('admin.credit.ledger.reverse')}
          </Button>
        ) : null}
      </td>
    </tr>
  );
}

/** İşaretli miktar: `+12`, `−9`, `0` (eksi işareti tipografik). */
function signedAmount(amount: number): string {
  if (amount > 0) return `+${amount}`;
  if (amount < 0) return `−${Math.abs(amount)}`;
  return '0';
}

function reasonText(t: ReturnType<typeof useTranslation>, entry: PanelCreditLedgerEntryDto) {
  if (entry.reason === TRIAL_PROVISION_REASON) {
    return t.t('admin.credit.ledger.reasons.trialProvision');
  }
  if (entry.reason) return entry.reason;
  switch (entry.kind) {
    case 'consume':
    case 'consume_contract':
    case 'refund':
    case 'expire':
      return t.t(`admin.credit.ledger.reasons.${entry.kind}`);
    default:
      return '—';
  }
}

export function ActorCell({ actor }: { actor: PanelActorDto }) {
  const t = useTranslation();
  if (actor.type === 'system') {
    return <span>{t.t('admin.credit.ledger.system')}</span>;
  }
  const name = actor.displayName ?? t.t('admin.credit.ledger.removedActor');
  return (
    <span className={styles.personCell}>
      <Avatar name={name} size="xs" />
      <span className={styles.personName}>{name}</span>
    </span>
  );
}
